// Copied from platform-app components/pos/ButtonCustomizerPopup.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronDown, Lock, Eye, Power, Palette } from 'lucide-react';
import { getIcon, CURATED_ICONS, AVAILABLE_ICONS } from '../../utils/iconRegistry';
import { type ButtonOverride, type ButtonAppearance, type ButtonRegistryEntry, BUTTON_REGISTRY, mergeButton } from '../../config/buttonRegistry';
import { resolveButtonLook } from '../../config/buttonTheme';
import { approvalOn } from './layout/ButtonAuthorizationTab';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ButtonCustomizerPopupProps {
  actionId: string;
  currentOverride?: ButtonOverride;
  onApply: (override: ButtonOverride) => void;
  onClose: () => void;
  previewContext: 'action-bar' | 'keypad' | 'more-popup' | 'header';
  /**
   * Defaults for a button that is not in BUTTON_REGISTRY (the POS Header
   * buttons). When omitted, BUTTON_REGISTRY[actionId] is used as before.
   */
  registryEntry?: ButtonRegistryEntry;
  /** Initial preview theme (Light/Dark). Defaults to 'light' — the popup's own toggle can flip it. */
  theme?: 'light' | 'dark';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#22c55e', '#059669', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#78716c', '#ffffff',
];

const GRADIENT_ANGLES = [0, 45, 90, 135, 180];

// ---------------------------------------------------------------------------
// Swatch grid sub-component
// ---------------------------------------------------------------------------

const SwatchGrid: React.FC<{
  selected: string;
  onSelect: (color: string) => void;
  customValue: string;
  onCustomChange: (value: string) => void;
}> = ({ selected, onSelect, customValue, onCustomChange }) => (
  <div>
    <div className="grid grid-cols-8 gap-1.5 mb-2">
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onSelect(c)}
          className={`w-8 h-8 rounded-lg border-2 transition-all ${
            selected.toLowerCase() === c.toLowerCase()
              ? 'border-stone-800 ring-2 ring-stone-400 scale-110'
              : c === '#ffffff'
                ? 'border-gray-300'
                : 'border-transparent hover:scale-105'
          }`}
          style={{ backgroundColor: c }}
          title={c}
        />
      ))}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-xs text-stone-500">Custom</span>
      <input
        type="text"
        value={customValue}
        onChange={(e) => {
          const v = e.target.value;
          onCustomChange(v);
          if (/^#[0-9a-fA-F]{6}$/.test(v)) onSelect(v);
        }}
        placeholder="#hex"
        maxLength={7}
        className="flex-1 px-2 py-1.5 text-xs bg-white border border-gray-300 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-500/50 focus:border-stone-500 transition-colors"
      />
      <div
        className="w-7 h-7 rounded-lg border border-gray-300 shrink-0"
        style={{ backgroundColor: /^#[0-9a-fA-F]{6}$/.test(customValue) ? customValue : selected }}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Collapsible Section (accordion — controlled by parent)
// ---------------------------------------------------------------------------

const CollapsibleSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ title, icon, isOpen, onToggle, children }) => {
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
      >
        {icon}
        <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide flex-1">{title}</span>
        <ChevronDown
          size={14}
          className={`text-stone-400 transition-transform ${isOpen ? '' : '-rotate-90'}`}
        />
      </button>
      {isOpen && <div className="px-4 py-4 space-y-4 border-t border-stone-200">{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ButtonCustomizerPopup: React.FC<ButtonCustomizerPopupProps> = ({
  actionId,
  currentOverride,
  onApply,
  onClose,
  previewContext,
  registryEntry,
  theme = 'light',
}) => {
  const registry = registryEntry ?? BUTTON_REGISTRY[actionId];
  const merged = mergeButton(actionId, currentOverride, registryEntry);

  // Preview theme toggle — starts from the editor's current preview theme,
  // but the merchant can flip it here to check both looks before applying.
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>(theme);

  // --- State ---
  const [label, setLabel] = useState(currentOverride?.label || '');
  const [icon, setIcon] = useState(currentOverride?.icon || '');
  const [bgColor, setBgColor] = useState(currentOverride?.bgColor || '');
  const [textColor, setTextColor] = useState(currentOverride?.textColor || '');
  const [iconColor, setIconColor] = useState(currentOverride?.iconColor || '');
  const [bgType, setBgType] = useState<'solid' | 'gradient'>(currentOverride?.bgType || 'solid');
  const [gradientFrom, setGradientFrom] = useState(currentOverride?.gradientFrom || '');
  const [gradientTo, setGradientTo] = useState(currentOverride?.gradientTo || '');
  const [gradientAngle, setGradientAngle] = useState(currentOverride?.gradientAngle || 135);

  const [requiresApproval, setRequiresApproval] = useState(approvalOn(actionId, currentOverride));
  const [visibility, setVisibility] = useState<'all' | 'admin' | 'employee'>(currentOverride?.visibility ?? 'all');
  const [active, setActive] = useState(currentOverride?.active ?? true);

  // Accordion — only one section open at a time, all closed by default
  const [openSection, setOpenSection] = useState<string | null>(null);
  const toggleSection = useCallback((id: string) => {
    setOpenSection(prev => prev === id ? null : id);
  }, []);

  const [iconQuery, setIconQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Custom hex inputs
  const [bgCustomHex, setBgCustomHex] = useState(currentOverride?.bgColor || '');
  const [textCustomHex, setTextCustomHex] = useState(currentOverride?.textColor || '');
  const [iconCustomHex, setIconCustomHex] = useState(currentOverride?.iconColor || '');
  const [gradFromCustomHex, setGradFromCustomHex] = useState(currentOverride?.gradientFrom || '');
  const [gradToCustomHex, setGradToCustomHex] = useState(currentOverride?.gradientTo || '');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Live preview ---
  const previewAppearance = useMemo((): ButtonAppearance => {
    const edits: ButtonOverride = {};
    if (label) edits.label = label;
    if (icon) edits.icon = icon;
    if (bgColor) edits.bgColor = bgColor;
    if (textColor) edits.textColor = textColor;
    if (iconColor) edits.iconColor = iconColor;
    if (bgType === 'gradient') {
      edits.bgType = 'gradient';
      if (gradientFrom) edits.gradientFrom = gradientFrom;
      if (gradientTo) edits.gradientTo = gradientTo;
      edits.gradientAngle = gradientAngle;
    }
    return mergeButton(actionId, edits, registryEntry) || registry;
  }, [actionId, label, icon, bgColor, textColor, iconColor, bgType, gradientFrom, gradientTo, gradientAngle, registry, registryEntry]);

  // Draft override for the theme resolver — colour keys only, and only ones
  // the merchant has actually touched. When none are set, resolveButtonLook
  // falls back to the theme default so the preview follows the toggle; once
  // any colour is set, the resolver keeps those colours on both themes
  // (verbatim rule: user colours never revert when the theme changes).
  const draftColourOverride = useMemo((): ButtonOverride => {
    const edits: ButtonOverride = {};
    if (bgType === 'gradient') {
      edits.bgType = 'gradient';
      if (gradientFrom) edits.gradientFrom = gradientFrom;
      if (gradientTo) edits.gradientTo = gradientTo;
      edits.gradientAngle = gradientAngle;
    } else if (bgColor) {
      edits.bgColor = bgColor;
    }
    if (textColor) edits.textColor = textColor;
    if (iconColor) edits.iconColor = iconColor;
    return edits;
  }, [bgColor, textColor, iconColor, bgType, gradientFrom, gradientTo, gradientAngle]);

  const previewLook = useMemo(
    () => resolveButtonLook(actionId, draftColourOverride, previewTheme, undefined, registryEntry),
    [actionId, draftColourOverride, previewTheme, registryEntry],
  );

  // --- Debounced icon search ---
  const handleIconQueryChange = useCallback((q: string) => {
    setIconQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(q), 200);
  }, []);

  const filteredIcons = useMemo(() => {
    if (!debouncedQuery.trim()) return null; // null = show curated categories
    const q = debouncedQuery.toLowerCase();
    return AVAILABLE_ICONS.filter(name => name.toLowerCase().includes(q)).slice(0, 80);
  }, [debouncedQuery]);

  // Render a single icon cell in the picker grid
  const renderIconCell = useCallback((name: string) => {
    const IconComp = getIcon(name);
    return (
      <button
        key={name}
        type="button"
        onClick={() => setIcon(name)}
        title={name}
        className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors ${
          icon === name
            ? 'bg-accent-100 ring-2 ring-accent-500 text-accent-700'
            : 'bg-stone-50 hover:bg-stone-100 text-stone-600'
        }`}
      >
        <IconComp size={16} />
      </button>
    );
  }, [icon]);

  // --- Color handlers ---
  const handleBgSelect = (c: string) => { setBgColor(c); setBgCustomHex(c); };
  const handleTextColorSelect = (c: string) => { setTextColor(c); setTextCustomHex(c); };
  const handleIconColorSelect = (c: string) => { setIconColor(c); setIconCustomHex(c); };
  const handleGradFromSelect = (c: string) => { setGradientFrom(c); setGradFromCustomHex(c); };
  const handleGradToSelect = (c: string) => { setGradientTo(c); setGradToCustomHex(c); };

  // --- Apply: only emit overrides that differ from registry ---
  const handleApply = () => {
    const override: ButtonOverride = {};
    if (label && label !== registry.label) override.label = label;
    if (icon && icon !== registry.icon) override.icon = icon;
    if (bgType === 'gradient') {
      override.bgType = 'gradient';
      if (gradientFrom) override.gradientFrom = gradientFrom;
      if (gradientTo) override.gradientTo = gradientTo;
      override.gradientAngle = gradientAngle;
    } else if (bgColor && bgColor !== registry.bgColor) {
      override.bgColor = bgColor;
    }
    if (textColor && textColor !== registry.textColor) override.textColor = textColor;
    if (iconColor && iconColor !== registry.iconColor) override.iconColor = iconColor;
    if (requiresApproval) override.requiresApproval = true;
    // Void needs a manager when unset, so "off" must be saved explicitly.
    else if (actionId === 'void') override.requiresApproval = false;
    if (visibility !== 'all') override.visibility = visibility;
    if (active === false) override.active = false;
    {
    }
    onApply(override);
  };

  // --- Preview renderer ---
  // Background/border/text colour come from resolveButtonLook (theme default,
  // unless the merchant has set colours of their own); label/icon come from
  // the draft appearance so typing a label or picking an icon still live-updates.
  const renderPreview = () => {
    const bgStyle = previewLook.style;
    const textColor = (previewLook.style.color as string) || previewAppearance.textColor;
    const IconComp = getIcon(previewAppearance.icon);

    if (previewContext === 'action-bar') {
      return (
        <div
          className="inline-flex items-center gap-2 px-4 rounded-full shadow-lg"
          style={{ ...bgStyle, height: 38 }}
        >
          {React.createElement(IconComp, { size: 16, color: previewLook.fg })}
          <span style={{ color: textColor, fontSize: 12, fontWeight: 600 }}>
            {previewAppearance.label}
          </span>
        </div>
      );
    }

    if (previewContext === 'header') {
      // Sale-screen header: a square icon-only tile beside the search box.
      return (
        <div className="flex flex-col items-center gap-1.5">
          <div
            className="flex items-center justify-center rounded-xl shadow-lg"
            style={{ ...bgStyle, width: 48, height: 48 }}
            title={previewAppearance.label}
          >
            {React.createElement(IconComp, { size: 24, color: previewLook.fg })}
          </div>
          <span style={{ color: previewTheme === 'light' ? '#52525b' : '#a1a1aa', fontSize: 10, fontWeight: 600 }}>
            {previewAppearance.label}
          </span>
        </div>
      );
    }

    if (previewContext === 'keypad') {
      return (
        <div
          className="flex flex-col items-center justify-center gap-1 rounded-xl shadow-lg"
          style={{ ...bgStyle, width: 72, height: 72 }}
        >
          {React.createElement(IconComp, { size: 22, color: previewLook.fg })}
          <span
            className="text-center px-1 truncate"
            style={{ color: textColor, fontSize: 10, fontWeight: 600, maxWidth: 64, lineHeight: '1.1' }}
          >
            {previewAppearance.label}
          </span>
        </div>
      );
    }

    // more-popup
    return (
      <div className="flex flex-col items-center gap-1.5" style={{ width: 64 }}>
        <div
          className="flex items-center justify-center rounded-full shadow-lg"
          style={{ ...bgStyle, width: 44, height: 44 }}
        >
          {React.createElement(IconComp, { size: 20, color: previewLook.fg })}
        </div>
        <span
          className="text-center truncate"
          style={{ color: textColor, fontSize: 10, fontWeight: 600, maxWidth: 60 }}
        >
          {previewAppearance.label}
        </span>
      </div>
    );
  };

  // --- Render ---
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Card */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200 rounded-t-2xl">
          <h3 className="text-base font-semibold text-stone-800">Customize Button</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* -- Live Preview -- */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Preview</p>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden" role="group" aria-label="Preview theme">
                <button
                  type="button"
                  onClick={() => setPreviewTheme('light')}
                  aria-pressed={previewTheme === 'light'}
                  className={`px-3 min-h-[44px] text-xs font-semibold transition-colors ${
                    previewTheme === 'light' ? 'bg-stone-800 text-white' : 'bg-white text-stone-500 hover:bg-stone-50'
                  }`}
                >
                  Light
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewTheme('dark')}
                  aria-pressed={previewTheme === 'dark'}
                  className={`px-3 min-h-[44px] text-xs font-semibold border-l border-gray-300 transition-colors ${
                    previewTheme === 'dark' ? 'bg-stone-800 text-white' : 'bg-white text-stone-500 hover:bg-stone-50'
                  }`}
                >
                  Dark
                </button>
              </div>
            </div>
            <div
              className="flex items-center justify-center rounded-xl py-4"
              style={{ backgroundColor: previewTheme === 'light' ? '#f4f4f5' : '#111' }}
            >
              {renderPreview()}
            </div>
          </div>

          {/* -- Label (always visible, not collapsible) -- */}
          <div>
            <label className="block text-xs font-medium text-stone-500 uppercase tracking-wide mb-1.5">Label</label>
            <input
              type="text"
              value={label || previewAppearance.label}
              onChange={(e) => setLabel(e.target.value.slice(0, 13))}
              maxLength={13}
              placeholder={registry.label}
              className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-500/50 focus:border-stone-500 transition-colors min-h-[44px]"
            />
            <p className="text-[11px] text-stone-400 mt-1">{(label || previewAppearance.label).length}/13 characters</p>
          </div>

          {/* -- 1. Permissions & Visibility -- */}
          <CollapsibleSection title="Permissions & Visibility" icon={<Lock size={14} className="text-stone-500" />} isOpen={openSection === 'permissions'} onToggle={() => toggleSection('permissions')}>
            {/* Require Manager Approval */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-amber-500" />
                <div>
                  <div className="text-sm font-medium text-stone-700">Require Manager Approval</div>
                  <div className="text-[11px] text-stone-400">Manager PIN required before executing</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRequiresApproval(!requiresApproval)}
                className={`relative w-10 h-6 rounded-full transition-colors ${requiresApproval ? 'bg-accent-500' : 'bg-stone-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${requiresApproval ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>

            {/* Visibility */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Eye size={14} className="text-blue-500" />
                <div className="text-sm font-medium text-stone-700">Visibility</div>
              </div>
              <div className="flex gap-2">
                {([
                  { value: 'all' as const, label: 'Everyone' },
                  { value: 'admin' as const, label: 'Manager Only' },
                  { value: 'employee' as const, label: 'Employee Only' },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVisibility(opt.value)}
                    className={`flex-1 py-2 text-xs font-medium rounded-lg border transition-all ${
                      visibility === opt.value
                        ? 'bg-accent-600 text-white border-accent-600'
                        : 'bg-white text-stone-600 border-gray-300 hover:border-stone-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Active */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Power size={14} className="text-accent-500" />
                <div>
                  <div className="text-sm font-medium text-stone-700">Active</div>
                  <div className="text-[11px] text-stone-400">Disable to hide from register without removing</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActive(!active)}
                className={`relative w-10 h-6 rounded-full transition-colors ${active ? 'bg-accent-500' : 'bg-stone-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${active ? 'left-[18px]' : 'left-0.5'}`} />
              </button>
            </div>
          </CollapsibleSection>

          {/* -- 2. Background Color -- */}
          <CollapsibleSection title="Background Color" icon={<Palette size={14} className="text-stone-500" />} isOpen={openSection === 'bg'} onToggle={() => toggleSection('bg')}>
            <div className="flex bg-stone-100 rounded-lg p-0.5 mb-3">
              {(['solid', 'gradient'] as const).map((mode) => (
                <button key={mode} type="button" onClick={() => setBgType(mode)}
                  className={`flex-1 text-xs font-medium py-2 rounded-md transition-all ${bgType === mode ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                  {mode === 'solid' ? 'Solid' : 'Gradient'}
                </button>
              ))}
            </div>
            {bgType === 'solid' ? (
              <SwatchGrid selected={bgColor || registry.bgColor} onSelect={handleBgSelect} customValue={bgCustomHex} onCustomChange={setBgCustomHex} />
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-stone-500 mb-1">From</p>
                  <SwatchGrid selected={gradientFrom || registry.bgColor} onSelect={handleGradFromSelect} customValue={gradFromCustomHex} onCustomChange={setGradFromCustomHex} />
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1">To</p>
                  <SwatchGrid selected={gradientTo || '#3b82f6'} onSelect={handleGradToSelect} customValue={gradToCustomHex} onCustomChange={setGradToCustomHex} />
                </div>
                <div>
                  <p className="text-xs text-stone-500 mb-1">Angle</p>
                  <div className="flex gap-1.5">
                    {GRADIENT_ANGLES.map((a) => (
                      <button key={a} type="button" onClick={() => setGradientAngle(a)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${gradientAngle === a ? 'bg-accent-600 text-white border-accent-600' : 'bg-white text-stone-600 border-gray-300 hover:border-stone-400'}`}>
                        {a}&deg;
                      </button>
                    ))}
                  </div>
                </div>
                <div className="h-6 rounded-lg border border-gray-200"
                  style={{ background: `linear-gradient(${gradientAngle}deg, ${gradientFrom || registry.bgColor}, ${gradientTo || '#3b82f6'})` }} />
              </div>
            )}
          </CollapsibleSection>

          {/* -- 3. Text Color -- */}
          <CollapsibleSection title="Text Color" icon={<Palette size={14} className="text-stone-500" />} isOpen={openSection === 'text'} onToggle={() => toggleSection('text')}>
            <SwatchGrid selected={textColor || registry.textColor} onSelect={handleTextColorSelect} customValue={textCustomHex} onCustomChange={setTextCustomHex} />
          </CollapsibleSection>

          {/* -- 4. Icon Color -- */}
          <CollapsibleSection title="Icon Color" icon={<Palette size={14} className="text-stone-500" />} isOpen={openSection === 'iconColor'} onToggle={() => toggleSection('iconColor')}>
            <SwatchGrid selected={iconColor || registry.iconColor} onSelect={handleIconColorSelect} customValue={iconCustomHex} onCustomChange={setIconCustomHex} />
          </CollapsibleSection>

          {/* -- 5. Icon -- */}
          <CollapsibleSection title="Icon" icon={<Search size={14} className="text-stone-500" />} isOpen={openSection === 'icon'} onToggle={() => toggleSection('icon')}>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input type="text" value={iconQuery} onChange={(e) => handleIconQueryChange(e.target.value)}
                placeholder="Search icons..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-500/50 focus:border-stone-500 transition-colors" />
            </div>
            <div className="max-h-[200px] overflow-y-auto border border-stone-200 rounded-xl p-2">
              {filteredIcons ? (
                filteredIcons.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-4">No icons found</p>
                ) : (
                  <div className="flex flex-wrap gap-1">{filteredIcons.map(renderIconCell)}</div>
                )
              ) : (
                Object.entries(CURATED_ICONS).map(([category, icons]) => (
                  <div key={category} className="mb-3">
                    <p className="text-[10px] text-stone-500 uppercase tracking-wider font-bold mb-1 px-1">{category}</p>
                    <div className="flex flex-wrap gap-1">
                      {icons.map(name => { try { return renderIconCell(name); } catch { return null; } })}
                    </div>
                  </div>
                ))
              )}
            </div>
            {icon && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-stone-500">Selected: <span className="font-medium text-stone-700">{icon}</span></span>
                {(() => { const Ic = getIcon(icon); return <Ic size={16} className="text-stone-700" />; })()}
                <button type="button" onClick={() => setIcon('')} className="text-xs text-red-500 hover:text-red-700 ml-auto">Clear</button>
              </div>
            )}
          </CollapsibleSection>
        </div>

        {/* -- Footer with Reset + Apply -- */}
        <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-200 rounded-b-2xl">
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => { onApply({}); onClose(); }}
              style={{ flex: 1, padding: 10, border: '1px solid #444', borderRadius: 8, background: 'transparent', color: '#888', fontSize: 13, cursor: 'pointer' }}
            >
              Reset to theme colours
            </button>
            <button
              type="button"
              onClick={handleApply}
              style={{ flex: 1, padding: 10, background: '#22c55e', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ButtonCustomizerPopup;
